import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, 
  TrendingDown, 
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  PieChart,
  History,
  User,
  Crown,
  Calendar
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SharedPortfolioPage = () => {
  const { shareToken } = useParams();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSharedPortfolio = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/shared/${shareToken}`);
        setPortfolio(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('This portfolio is no longer shared or the link is invalid.');
        } else {
          setError('Failed to load portfolio. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSharedPortfolio();
  }, [shareToken]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    const prefix = value >= 0 ? '+' : '';
    return `${prefix}${value?.toFixed(2) || '0.00'}%`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <div className="animate-pulse-subtle text-primary font-serif text-xl">Loading portfolio...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingDown className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="font-serif text-xl text-[#1F2937] mb-2">Portfolio Not Found</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-[#1A4D2E] hover:bg-[#143d24]">
                Go to FictoInvest
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Header */}
      <header className="bg-white border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#1A4D2E] rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif text-xl font-semibold text-[#1A4D2E]">FictoInvest</span>
          </Link>
          <Link to="/register">
            <Button className="bg-[#1A4D2E] hover:bg-[#143d24]">
              Start Your Portfolio
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Portfolio Owner Info */}
        <Card className="bg-white border-stone-100 shadow-card mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-[#1A4D2E] to-[#2D5A3D] rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-serif text-2xl text-[#1F2937]">{portfolio.nickname}</h1>
                  {portfolio.tier === 'paid' && (
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                      <Crown className="w-3 h-3 mr-1" />
                      Pro
                    </Badge>
                  )}
                </div>
                {portfolio.member_since && (
                  <p className="text-gray-500 flex items-center gap-1 mt-1">
                    <Calendar className="w-4 h-4" />
                    Member since {portfolio.member_since}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Portfolio Value */}
        {portfolio.portfolio_value !== undefined && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="bg-white border-stone-100 shadow-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Portfolio Value</p>
                    <p className="text-3xl font-bold text-[#1F2937] tabular-nums mt-1">
                      {formatCurrency(portfolio.portfolio_value)}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-[#1A4D2E]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-stone-100 shadow-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Total P&L</p>
                    <p className={`text-3xl font-bold tabular-nums mt-1 ${portfolio.total_pl >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {formatCurrency(portfolio.total_pl)}
                    </p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${portfolio.total_pl >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    {portfolio.total_pl >= 0 ? (
                      <ArrowUpRight className="w-6 h-6 text-green-600" />
                    ) : (
                      <ArrowDownRight className="w-6 h-6 text-red-500" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-stone-100 shadow-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Return</p>
                    <p className={`text-3xl font-bold tabular-nums mt-1 ${portfolio.total_pl_percent >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {formatPercent(portfolio.total_pl_percent)}
                    </p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${portfolio.total_pl_percent >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    {portfolio.total_pl_percent >= 0 ? (
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    ) : (
                      <TrendingDown className="w-6 h-6 text-red-500" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Positions */}
        {portfolio.positions && portfolio.positions.length > 0 && (
          <Card className="bg-white border-stone-100 shadow-card mb-8">
            <CardHeader>
              <CardTitle className="font-serif text-xl text-[#1F2937] flex items-center gap-2">
                <PieChart className="w-5 h-5" />
                Holdings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left border-b border-stone-100">
                      <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Symbol</th>
                      <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Shares</th>
                      <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Current Price</th>
                      <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold text-right">Market Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portfolio.positions.map((pos) => (
                      <tr key={pos.symbol} className="border-b border-stone-50">
                        <td className="py-4">
                          <p className="font-semibold text-[#1F2937]">{pos.symbol}</p>
                        </td>
                        <td className="py-4">
                          <p className="tabular-nums">{pos.shares.toFixed(4)}</p>
                        </td>
                        <td className="py-4">
                          <p className="tabular-nums">{formatCurrency(pos.current_price)}</p>
                        </td>
                        <td className="py-4 text-right">
                          <p className="font-medium tabular-nums">{formatCurrency(pos.market_value)}</p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Transactions */}
        {portfolio.recent_transactions && portfolio.recent_transactions.length > 0 && (
          <Card className="bg-white border-stone-100 shadow-card">
            <CardHeader>
              <CardTitle className="font-serif text-xl text-[#1F2937] flex items-center gap-2">
                <History className="w-5 h-5" />
                Recent Transactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left border-b border-stone-100">
                      <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Date</th>
                      <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Type</th>
                      <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Symbol</th>
                      <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Shares</th>
                      <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portfolio.recent_transactions.map((tx, index) => (
                      <tr key={index} className="border-b border-stone-50">
                        <td className="py-4">
                          <p className="text-sm text-gray-500">
                            {new Date(tx.executed_at).toLocaleDateString()}
                          </p>
                        </td>
                        <td className="py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            tx.trade_type === 'buy' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {tx.trade_type.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-4">
                          <p className="font-semibold text-[#1F2937]">{tx.symbol}</p>
                        </td>
                        <td className="py-4">
                          <p className="tabular-nums">{tx.shares.toFixed(4)}</p>
                        </td>
                        <td className="py-4 text-right">
                          <p className={`font-medium tabular-nums ${tx.trade_type === 'buy' ? 'text-red-500' : 'text-green-600'}`}>
                            {tx.trade_type === 'buy' ? '-' : '+'}{formatCurrency(tx.total)}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA */}
        <Card className="bg-gradient-to-r from-[#1A4D2E] to-[#2D5A3D] border-0 mt-8">
          <CardContent className="p-8 text-center">
            <h2 className="font-serif text-2xl text-white mb-2">Want to build your own portfolio?</h2>
            <p className="text-green-100 mb-6">Start with $10,000 in virtual money and practice investing risk-free.</p>
            <Link to="/register">
              <Button className="bg-[#C1F23D] text-[#1A4D2E] hover:bg-[#d4ff5c] font-semibold px-8">
                Create Free Account
              </Button>
            </Link>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-stone-100 mt-12 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-gray-500 text-sm">
          <p>FictoInvest is a paper trading simulator. Not financial advice.</p>
        </div>
      </footer>
    </div>
  );
};

export default SharedPortfolioPage;
